import { useViewport } from 'contexts/viewport';
import { useTranslation } from 'react-i18next';
import {
    commonDescendingComparator,
    getLatestConfirmedId,
    getSubmissionGradedBy,
    getSubmissionGradingTimeInSeconds,
    getTooltipComponent,
    getUserPreferredFullName,
} from 'utils/helper';

export const useSubmissionsTableColumns = () => {
    const { t } = useTranslation();

    return [
        {
            id: 'filename',
            columnTitle: t('table.file-name', 'File Name'),
            isFixed: true,
            comparator: (order, submissions, _) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    return submission.files[0].original_file
                        ? submission.files[0].original_file.name
                        : submission.files[0].name;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'status',
            columnTitle: t('table.status', 'Status'),
            comparator: (order, submissions, _) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    if (submission.grading) {
                        return 'Reviewed';
                    }
                    if (submission.ai) {
                        return 'Ready for review';
                    }
                    if (submission.is_failed) {
                        return 'Failed to process';
                    }
                    return null;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'grade',
            columnTitle: t('table.final-grade', 'Final Grade'),
            ascSuffix: '(asc)',
            descSuffix: '(desc)',
            comparator: (order, submissions, { assignment }) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    if (!submission.grading || !assignment.ai_grading_enabled) return '--';
                    const score = Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
                        const { rate } = assignment.rubric.criteria
                            .find(({ id }) => id === criterionId)
                            .options.find(({ id }) => id === optionId);
                        return acc + Number(rate);
                    }, 0);
                    const maxScore = assignment.rubric.criteria.reduce((acc, criterion) => {
                        return acc + Math.max(...criterion.options.map(({ rate }) => Number(rate)));
                    }, 0);
                    return `${score}/${maxScore}`;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'time',
            columnTitle: t('table.grading-time-mmss', 'Grading Time (mm:ss)'),
            ascSuffix: '(asc)',
            descSuffix: '(desc)',
            tooltip: getTooltipComponent(
                t('assignment:submission.how-is-grading-time-measured', 'How is grading time measured?'),
                t(
                    'assignment:submission.how-is-grading-time-measured-description',
                    'Grading time is tracked only while you are active on the grading page. If you switch to another window or tab, tracking pauses. This metric may not accurately reflect grading time, as interruptions or background activity can affect tracking. Use it as an estimate alongside other data.'
                )
            ),
            comparator: (order, submissions) => {
                if (!order) return submissions;
                const getter = (submission) => getSubmissionGradingTimeInSeconds(submission);

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'grader',
            columnTitle: t('table.graded-by', 'Graded By'),
            comparator: (order, submissions, { accountUsers }) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    return getSubmissionGradedBy(submission, accountUsers);
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
    ];
};

export const useApiTableColumns = (submissions) => {
    const { t } = useTranslation();
    const { device } = useViewport();

    const isMultiFile = submissions?.some((submission) => submission.files.length > 1);

    return [
        {
            id: 'identifier',
            columnTitle: t('table.identifier', 'Identifier'),
            isFixed: true,
            comparator: (order, submissions, _) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    return submission?.api?.user?.name;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'filename',
            columnTitle: t('table.file-name', 'File Name'),
            isFixed: device === 'desktop',
            comparator: isMultiFile
                ? undefined
                : (order, submissions, _) => {
                      if (!order) return submissions;
                      const getter = (submission) => {
                          return submission.files[0].original_file
                              ? submission.files[0].original_file.name
                              : submission.files[0].name;
                      };

                      if (order === 'desc') {
                          const sortedSubmissions = [...submissions].sort((a, b) =>
                              commonDescendingComparator(a, b, getter)
                          );
                          return sortedSubmissions;
                      }
                      const sortedSubmissions = [...submissions].sort(
                          (a, b) => -commonDescendingComparator(a, b, getter)
                      );
                      return sortedSubmissions;
                  },
        },
        {
            id: 'section',
            columnTitle: t('table.section', 'Section'),
            comparator: (order, submissions) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    return submission.api.user.sections
                        ? submission.api.user.sections.map(({ name }) => name).join(', ')
                        : submission.api.user.section.name;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'status',
            columnTitle: t('table.status', 'Status'),
            comparator: (order, submissions, _) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    if (submission.grading) {
                        return 'Reviewed';
                    }
                    if (submission.ai) {
                        return 'Ready for review';
                    }
                    if (submission.is_failed) {
                        return 'Failed to process';
                    }
                    return null;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'grade',
            columnTitle: t('table.final-grade', 'Final Grade'),
            comparator: (order, submissions, assignment) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    if (!submission.grading || !assignment.ai_grading_enabled) return '--';
                    const score = Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
                        const { rate } = assignment.rubric.criteria
                            .find(({ id }) => id === criterionId)
                            .options.find(({ id }) => id === optionId);
                        return acc + Number(rate);
                    }, 0);
                    const maxScore = assignment.rubric.criteria.reduce((acc, criterion) => {
                        return acc + Math.max(...criterion.options.map(({ rate }) => Number(rate)));
                    }, 0);
                    return `${score}/${maxScore}`;
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'time',
            columnTitle: t('table.grading-time-mmss', 'Grading Time (mm:ss)'),
            ascSuffix: '(asc)',
            descSuffix: '(desc)',
            tooltip: getTooltipComponent(
                t('assignment:submission.how-is-grading-time-measured', 'How is grading time measured?'),
                t(
                    'assignment:submission.how-is-grading-time-measured-description',
                    'Grading time is tracked only while you are active on the grading page. If you switch to another window or tab, tracking pauses. This metric may not accurately reflect grading time, as interruptions or background activity can affect tracking. Use it as an estimate alongside other data.'
                )
            ),
            comparator: (order, submissions) => {
                if (!order) return submissions;
                const getter = (submission) => getSubmissionGradingTimeInSeconds(submission);

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
        {
            id: 'grader',
            columnTitle: t('table.graded-by', 'Graded By'),
            comparator: (order, submissions, { accountUsers }) => {
                if (!order) return submissions;
                const getter = (submission) => {
                    const graderOwnerAccountId = getLatestConfirmedId(submission.sessions);
                    const graderAccountUser = accountUsers.find(
                        ({ owner_account_id }) => owner_account_id === graderOwnerAccountId
                    );
                    return getUserPreferredFullName(graderAccountUser);
                };

                if (order === 'desc') {
                    const sortedSubmissions = [...submissions].sort((a, b) => commonDescendingComparator(a, b, getter));
                    return sortedSubmissions;
                }
                const sortedSubmissions = [...submissions].sort((a, b) => -commonDescendingComparator(a, b, getter));
                return sortedSubmissions;
            },
        },
    ];
};
